import { useState, useContext, createContext } from "react";
import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const API = `${BACKEND_URL}/api`;

// App Logos (TopIt Master Toolkit)

export const APP_LOGO = "/topit-logo.png"; // full wordmark (Top=navy, It=red) — new master logo

export const APP_LOGO_ICON = "/topit-icon.png"; // icon only (red circle + arrow + bars) — from master logo

export const APP_LOGO_POWERED = "/topit-logo.png"; // full wordmark (transparent) used on landing hero

export const APP_LOGO_WHITE = "/topit-logo-white.png"; // white-text wordmark for dark backgrounds

// Auth Context

export const AuthContext = createContext(null);


export const useAuth = () => useContext(AuthContext);

// Provider data with brand colors and logos

export const PROVIDERS = [
  { 
    name: "MTN", 
    color: "#FFCC00", 
    textColor: "#000", 
    logo: "https://static.prod-images.emergentagent.com/jobs/ab353935-b766-4656-b703-14eedbc1cc93/images/aeb5a60062236dd1ab162c28d147e64a4f8d9f843ab1709e1f0c4ec571a40205.png",
    logoScale: 1
  },
  { 
    name: "Airtel", 
    color: "#E60000", 
    textColor: "#fff", 
    logo: "https://static.prod-images.emergentagent.com/jobs/ab353935-b766-4656-b703-14eedbc1cc93/images/962be7abfaf3fb6bffcdb43d683a90eedcf7e141df7abf23197e192e7dccb257.png",
    logoScale: 5.0
  },
  { 
    name: "Glo", 
    color: "#009933", 
    textColor: "#fff", 
    logo: "https://static.prod-images.emergentagent.com/jobs/ab353935-b766-4656-b703-14eedbc1cc93/images/40294ec2f741c0421d3ed9a1615e43fa9afc773bf5b769cc6a3aa895876338cc.png",
    logoScale: 1
  },
  { 
    name: "9mobile", 
    color: "#006B3F", 
    textColor: "#fff", 
    logo: "https://static.prod-images.emergentagent.com/jobs/ab353935-b766-4656-b703-14eedbc1cc93/images/0ef5f1b926e1cb25c00f0cd863283422d779ea7f45aa22de24f5ebe36394158f.png",
    logoScale: 5.2
  },
];

// Small network-logo avatar (with optional service-type corner badge), reused in tx lists.

export const NetworkLogoAvatar = ({ provider, size = 40, serviceType }) => {
  const p = PROVIDERS.find((x) => x.name === provider);
  const scale = p?.logoScale || 1;
  const logoSize = Math.round(size * 0.55 * scale);
  return (
    <div className="relative flex-shrink-0" data-testid={`network-badge-${provider || "unknown"}`}>
      <div
        className="rounded-xl flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size, backgroundColor: p?.color || "#e2e8f0" }}
      >
        {p?.logo ? (
          <img src={p.logo} alt={provider} className="object-contain bg-white rounded-lg" style={{ width: logoSize, height: logoSize }} />
        ) : (
          <span className="font-bold text-xs" style={{ color: p?.textColor || "#0f172a" }}>{(provider || "?").substring(0, 2)}</span>
        )}
      </div>
      {serviceType && (
        <span
          className={`absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-full flex items-center justify-center ring-2 ring-white ${serviceType === "data" ? "bg-emerald-500" : "bg-primary-custom"}`}
          title={serviceType === "data" ? "Data" : "Airtime"}
        >
          <Icon name={serviceType === "data" ? "wifi" : "smartphone"} className="text-white" style={{ fontSize: "11px" }} />
        </span>
      )}
    </div>
  );
};

// Compute applicable cashback/discount rate (percent) for a service + provider, from public rates.

export const computeCashbackRate = (rates, serviceType, provider) => {
  if (!rates || rates.cashback_enabled === false) return 0;
  const svc = rates[serviceType] || {};
  const providers = svc.providers || {};
  if (provider && providers[provider] != null) return Number(providers[provider]) || 0;
  return Number(svc.rate) || 0;
};

// Small "Earn X% cashback" badge shown on top-up buttons.

export const CashbackBadge = ({ rates, serviceType, provider, className = "" }) => {
  const rate = computeCashbackRate(rates, serviceType, provider);
  if (!rate || rate <= 0) return null;
  const label = rate % 1 === 0 ? rate : rate.toFixed(1);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-bold whitespace-nowrap ${className}`}
      data-testid="cashback-badge"
    >
      <Icon name="redeem" className="text-sm" /> Earn {label}% cashback
    </span>
  );
};

// Per-service discount % (customer pays amount − X%), managed on the admin Products page.
export const serviceDiscountPct = (rates, serviceType) => {
  const d = rates?.product_discounts || {};
  const v = Number(d[serviceType]);
  return Number.isFinite(v) && v > 0 ? v : 0;
};

// Net price after a discount %.
export const discountedPrice = (amount, pct) => Math.round((Number(amount || 0) * (1 - (pct || 0) / 100)) * 100) / 100;

// "X% off" savings pill shown on airtime/data screens.
export const DiscountBadge = ({ rates, serviceType, className = "" }) => {
  const pct = serviceDiscountPct(rates, serviceType);
  if (!pct) return null;
  const label = pct % 1 === 0 ? pct : pct.toFixed(1);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-bold whitespace-nowrap ${className}`}
      data-testid="discount-badge"
    >
      <Icon name="sell" className="text-sm" /> {label}% off
    </span>
  );
};

// Configure axios to include auth token

export const api = axios.create({ baseURL: API, withCredentials: true });

// ---- Web Push helpers ----
export const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

export const pushSupported = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const pushPermission = () => (typeof Notification !== "undefined" ? Notification.permission : "unsupported");

export const enablePush = async () => {
  if (!pushSupported()) throw new Error("Notifications aren't supported on this device/browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications permission was not granted.");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    });
  }
  await api.post("/push/subscribe", { subscription: sub.toJSON ? sub.toJSON() : sub });
  return true;
};

// Is this browser already push-subscribed?
export const isPushSubscribed = async () => {
  try {
    if (!pushSupported()) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
};

// Turn OFF push on this device (unsubscribe + tell backend to prune it).
export const disablePush = async () => {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
    }
  } catch { /* noop */ }
  try { localStorage.setItem("topit_push_dismissed", "1"); } catch { /* noop */ }
  return true;
};

export const getPushPreferences = async () => (await api.get("/push/preferences")).data.preferences;
export const savePushPreferences = async (preferences) =>
  (await api.put("/push/preferences", { preferences })).data.preferences;

export const sendTestPush = async () => (await api.post("/push/test")).data;


api.interceptors.request.use((config) => {
  // Session normally lives in an httpOnly cookie (sent automatically). Legacy localStorage
  // tokens (pre-cookie migration) are still attached as a Bearer fallback until they expire.
  const token = localStorage.getItem("topit_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler: if the session cookie is missing/expired/invalid, every
// authed request returns 401 and the app would otherwise hang on infinite spinners.
// Clear the stale session and send the user back to login (auth calls are excluded so a
// wrong-password 401 doesn't wipe/redirect).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || "";
    const isAuthCall = /login|register|signup|forgot|reset|verify-otp|verify-email|change-password/i.test(url);
    if (error?.response?.status === 401 && !isAuthCall) {
      const hadSession = localStorage.getItem("topit_user") || localStorage.getItem("topit_token");
      localStorage.removeItem("topit_token");
      localStorage.removeItem("topit_user");
      try { localStorage.removeItem("topit_active_page"); } catch { /* noop */ }
      if (hadSession && !window.__topit_auth_redirecting) {
        window.__topit_auth_redirecting = true;
        try { localStorage.setItem("topit_session_expired", "1"); } catch { /* noop */ }
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

// Flutterwave should redirect back to the SAME origin the user is on (not a hardcoded domain)

export const paymentCallbackUrl = () => `${window.location.origin}/payment-callback`;

// Shared "how to install" instructions modal (iOS Safari vs other browsers)

export const Icon = ({ name, className = "", style = {} }) => (
  <span className={`material-symbols-outlined ${className}`} style={style}>{name}</span>
);

// Login Page Component

export const parsePlanValidity = (description) => {
  if (!description) return null;
  const m = description.match(/(\d+)\s*day/i);
  if (m) return parseInt(m[1], 10);
  return null;
};

// Group validity into bucket

export const validityBucket = (days) => {
  if (days == null) return "other";
  if (days <= 2) return "daily";
  if (days <= 14) return "weekly";
  return "monthly";
};


export const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

export const nairaK = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `₦${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `₦${v.toLocaleString()}`;
};

export const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Today";
  if (d.toDateString() === yest.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
};

export const providerMeta = (name) => PROVIDERS.find((p) => p.name === name) || { color: "#64748b", textColor: "#fff" };

// ---- Flutterwave Inline (in-app popup checkout) ----
let _flwPublicKeyPromise = null;
export const getFlutterwavePublicKey = () => {
  if (!_flwPublicKeyPromise) {
    _flwPublicKeyPromise = api
      .get("/payments/flutterwave/config")
      .then((r) => r.data.public_key)
      .catch(() => { _flwPublicKeyPromise = null; return null; });
  }
  return _flwPublicKeyPromise;
};

// Opens the secure Flutterwave modal in-app. tx_ref is generated by the backend.
// onSuccess fires on payment completion (then the caller must verify server-side);
// onClose fires if the user dismisses without completing.
export const payWithFlutterwaveInline = async ({ txRef, amount, customer = {}, title = "TopIt", description = "", onSuccess, onClose }) => {
  const publicKey = await getFlutterwavePublicKey();
  if (!publicKey || typeof window === "undefined" || !window.FlutterwaveCheckout) {
    throw new Error("Payment is unavailable right now. Please try again in a moment.");
  }
  let settled = false;
  const modal = window.FlutterwaveCheckout({
    public_key: publicKey,
    tx_ref: txRef,
    amount: Number(amount),
    currency: "NGN",
    // Omit payment_options so Flutterwave shows ALL methods enabled on the account
    // (card, USSD, bank, transfer, barter, etc.) like the standard inline modal.
    customer: {
      email: customer.email || "customer@topit.app",
      phonenumber: customer.phone || "",
      name: customer.name || "TopIt Customer",
    },
    customizations: {
      title,
      description,
      logo: window.location.origin + "/topit-flw-logo.png",
    },
    callback: (data) => {
      settled = true;
      try { modal && modal.close && modal.close(); } catch { /* noop */ }
      if (onSuccess) onSuccess(data);
    },
    onclose: () => { if (!settled && onClose) onClose(); },
  });
  return modal;
};



export const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Month selector pills (shared by both tabs)

export const MonthPills = ({ months, selected, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 py-1" data-testid="analytics-month-pills">
    {months.map((m) => {
      const active = m.key === selected;
      return (
        <button
          key={m.key}
          data-testid={`month-pill-${m.label}`}
          onClick={() => onSelect(m.key)}
          className={`shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all ${
            active ? "bg-primary-custom text-white shadow-md shadow-primary-custom/30" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"
          }`}
        >
          {m.label}
        </button>
      );
    })}
  </div>
);

// SVG line chart for hero card (white line on red)

export const HeroLineChart = ({ points }) => {
  const W = 340, H = 90, padX = 8, padY = 14;
  const max = Math.max(...points.map((p) => p.amount), 1);
  const coords = points.map((p, i) => {
    const x = padX + (i / (points.length - 1)) * (W - padX * 2);
    const y = H - padY - (p.amount / max) * (H - padY * 2);
    return [x, y];
  });
  const linePts = coords.map((c) => c.join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-24" preserveAspectRatio="none">
      <polyline points={linePts} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" vectorEffect="non-scaling-stroke" />
      {coords.map((c, i) => (
        <circle key={i} cx={c[0]} cy={c[1]} r="2.5" fill="white" />
      ))}
    </svg>
  );
};

// Donut chart for network usage (Overview tab)

export const NetworkDonut = ({ segments, total }) => {
  const r = 42, cx = 60, cy = 60, sw = 16;
  const C = 2 * Math.PI * r;
  let offset = 0;
  const hasData = segments.some((s) => s.pct > 0);
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F1F5F9" strokeWidth={sw} />
      {hasData && segments.map((s, i) => {
        const len = (s.pct / 100) * C;
        const seg = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={providerMeta(s.network).color}
            strokeWidth={sw}
            strokeDasharray={`${len} ${C - len}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += len;
        return seg;
      })}
      <text x={cx} y={cy - 2} textAnchor="middle" className="fill-slate-900" style={{ fontSize: "16px", fontWeight: 700 }}>{nairaK(total)}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" className="fill-slate-400" style={{ fontSize: "9px" }}>Total</text>
    </svg>
  );
};


export const SectionHeader = ({ title, action, onAction, testid }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="font-bold text-slate-900 text-lg">{title}</h3>
    {action && (
      <button onClick={onAction} data-testid={testid} className="text-sm font-semibold text-primary-custom flex items-center gap-0.5">
        {action} <Icon name="arrow_forward" className="text-base" />
      </button>
    )}
  </div>
);

// ---------- OVERVIEW TAB (design 1) ----------

export const CopyButton = ({ value, label = "Copy", className = "", testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={testid} className={className}>
      <Icon name={copied ? "check" : "content_copy"} className="text-base" />
      {copied ? "Copied!" : label}
    </button>
  );
};


export const NET_BADGE = {
  MTN: { bg: "bg-yellow-100", text: "text-yellow-800", dot: "#FFCC00" },
  Airtel: { bg: "bg-red-100", text: "text-red-700", dot: "#E60000" },
  Glo: { bg: "bg-green-100", text: "text-green-700", dot: "#009933" },
  "9mobile": { bg: "bg-emerald-100", text: "text-emerald-700", dot: "#006B3F" },
};


export const contactInitials = (name = "") => {
  const n = (name || "").trim();
  if (!n) return "?";
  if (/^\+?\d[\d\s-]*$/.test(n)) return n.replace(/\D/g, "").slice(-2) || "#";
  const parts = n.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || n[0].toUpperCase();
};


export const NetworkBadge = ({ provider, size = "sm" }) => {
  if (!provider) return null;
  const b = NET_BADGE[provider] || { bg: "bg-slate-100", text: "text-slate-600" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${b.bg} ${b.text} ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"}`} data-testid={`net-badge-${provider}`}>
      {b.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: b.dot }} />}
      {provider}
    </span>
  );
};


export const ContactAvatar = ({ name, color, size = "md" }) => {
  const sz = size === "lg" ? "w-14 h-14 text-lg" : size === "sm" ? "w-10 h-10 text-sm" : "w-12 h-12 text-base";
  return (
    <span className={`${sz} rounded-full flex items-center justify-center font-bold text-white shrink-0`} style={{ background: color || "#64748B" }}>
      {contactInitials(name)}
    </span>
  );
};

// Client-side network detection (mirrors backend prefixes)

export const CLIENT_NET_PREFIXES = {
  MTN: ["0803", "0806", "0703", "0706", "0813", "0816", "0810", "0814", "0903", "0906", "0913", "0916", "0704", "0707"],
  Airtel: ["0802", "0808", "0708", "0812", "0701", "0902", "0901", "0904", "0907", "0912", "0911"],
  Glo: ["0805", "0807", "0705", "0815", "0811", "0905", "0915"],
  "9mobile": ["0809", "0817", "0818", "0908", "0909"],
};

export function detectNetworkClient(phone) {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("234")) d = "0" + d.slice(3);
  else if (d.length === 10) d = "0" + d;
  const p = d.slice(0, 4);
  for (const [net, prefixes] of Object.entries(CLIENT_NET_PREFIXES)) {
    if (prefixes.includes(p)) return net;
  }
  return null;
}


export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Shared modal: shown after a Flutterwave recharge payment is initiated.
// Handles "I've paid -> verify -> vend" with idempotent backend verification.

export const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: "home", kind: "view" },
  { id: "airtime", label: "Airtime Top-Up", icon: "smartphone", kind: "view" },
  { id: "data", label: "Data Bundles", icon: "wifi", kind: "view" },
  { id: "cable_tv", label: "Cable TV", icon: "live_tv", kind: "view", soon: true },
  { id: "electricity", label: "Electricity", icon: "bolt", kind: "view", soon: true },
  { id: "transactions", label: "Transactions", icon: "receipt_long", kind: "view" },
  { id: "analytics", label: "Analytics", icon: "bar_chart", kind: "view" },
  { id: "referral", label: "Refer & Earn", icon: "card_giftcard", kind: "view" },
  { id: "family", label: "Family Mode", icon: "diversity_3", kind: "view" },
  { id: "discover", label: "Discover", icon: "local_activity", kind: "view" },
  { id: "beneficiaries", label: "Beneficiaries", icon: "groups", kind: "modal" },
  { id: "schedules", label: "Scheduled Recharges", icon: "schedule", kind: "modal" },
  { id: "contacts", label: "Contacts", icon: "contacts", kind: "modal" },
  { id: "wallet", label: "Wallet", icon: "account_balance_wallet", kind: "modal" },
  { id: "notifications_settings", label: "Notifications", icon: "notifications", kind: "modal" },
  { id: "settings", label: "Settings", icon: "settings", kind: "modal" },
  { id: "support", label: "Support", icon: "support_agent", kind: "view" },
];

// TopIt brand mark used in sidebar / header

export const TopItMark = ({ size = "md", variant = "light" }) => {
  const h = size === "sm" ? "h-7" : size === "lg" ? "h-11" : "h-9";
  const logo = variant === "light" ? APP_LOGO_WHITE : APP_LOGO;
  return (
    <div className="flex flex-col">
      <img src={logo} alt="TopIt" className={`${h} w-auto object-contain drop-shadow`} />
      <span className={`mt-1 text-[9px] tracking-wide leading-none ${variant === "light" ? "text-slate-400" : "text-slate-500"}`} data-testid="powered-by-sineli">
        Powered by Sineli
      </span>
    </div>
  );
};

// Sidebar component

export const StatCard = ({ label, value, icon, accent, testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-200/70 hover:shadow-md transition-shadow" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-slate-500 text-xs font-medium uppercase tracking-wide truncate">{label}</p>
        <p className="text-slate-900 text-xl lg:text-2xl font-bold mt-2 truncate">{value}</p>
        <p className="text-slate-400 text-xs mt-1">This Month</p>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${accent}`}>
        <Icon name={icon} className="text-base" />
      </div>
    </div>
  </div>
);

// Coming Soon placeholder
